/*
 * @Description: 实时定位驾驶舱 — 顶部筛选条
 *   左：日期选择器（默认当天，单天，可选历史）
 *   中：状态统计（实时态 4 项可互斥筛选 / 历史态仅展示当日活跃）
 *   右：地图点位入口（点位管理为后续期，先占位）
 *   首次点击选中某状态时弹「再次点击取消筛选」气泡，3s 后消失。
 */
import React, { useEffect, useRef, useState } from 'react'
import { Button, DatePicker, Tag } from 'antd'
import dayjs from 'dayjs'
import { STATUS_KEYS, StatusKey } from '../../constants'
import { RealtimeSummaryData } from '../../types/cockpit.types'
import AlertBell from '../AlertBell'
import RefreshControl from '../RefreshControl'
import './index.scss'

interface TopFilterBarProps {
  /** 当前选中日期 */
  selectedDate: dayjs.Dayjs
  /** 日期变化回调 */
  onChangeDate: (date: dayjs.Dayjs) => void
  /** A1 状态统计；null 表示尚未取到 */
  summary: RealtimeSummaryData | null
  /** 当前选中的状态筛选键；null 表示未筛选 */
  activeStatus: StatusKey | null
  /** 状态筛选变化回调（再次点击同一项传 null 取消） */
  onChangeStatus: (status: StatusKey | null) => void
  /** 点击「地图点位」 */
  onClickMapPoi: () => void
  /** 点击「定位设置」 */
  onClickSettings: () => void
  /** 当前刷新间隔（毫秒） */
  refreshIntervalMs: number
  /** 刷新间隔变化 */
  onChangeRefreshInterval: (ms: number) => void
  /** 立即刷新页面数据 */
  onRefreshNow: () => void
  /** 是否 mock 模式（显示角标提示） */
  isMock: boolean
}

/** 实时态状态项配置（顺序即展示顺序） */
const REALTIME_STATUS_META: Array<{
  key: StatusKey
  label: string
  /** 是否展示为「X/总数」形式 */
  withTotal?: boolean
}> = [
  { key: STATUS_KEYS.ONLINE, label: '团队在线', withTotal: true },
  { key: STATUS_KEYS.MOVING, label: '移动中' },
  { key: STATUS_KEYS.STAYING, label: '停留中' },
  { key: STATUS_KEYS.OFFLINE, label: '离线' }
]

/** 从实时态统计里取某状态的计数 */
function getRealtimeCount(
  summary: RealtimeSummaryData,
  key: StatusKey
): number {
  if (!summary.isRealtime) return 0
  switch (key) {
    case STATUS_KEYS.ONLINE:
      return summary.online
    case STATUS_KEYS.MOVING:
      return summary.moving
    case STATUS_KEYS.STAYING:
      return summary.staying
    case STATUS_KEYS.OFFLINE:
      return summary.offline
    default:
      return 0
  }
}

/** 各状态图标：在线绿点；移动/停留/离线为设计提供的内联 SVG（自带彩色圆 + 符号） */
function StatusIcon({ statusKey }: { statusKey: StatusKey }) {
  switch (statusKey) {
    case STATUS_KEYS.ONLINE:
      return <span className="cockpit-status__dot" />
    case STATUS_KEYS.MOVING:
      return (
        <svg
          className="cockpit-status__svg"
          width="14"
          height="14"
          viewBox="0 0 12 12"
          fill="none"
        >
          <path
            d="M6 12C9.31371 12 12 9.31371 12 6C12 2.68629 9.31371 0 6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12Z"
            fill="#E3FCE4"
          />
          <path
            d="M3.95513 4.90884L5.24785 3.96988C5.39294 3.8637 5.56942 3.80917 5.74912 3.815C5.96835 3.82044 6.18043 3.89406 6.35589 4.02561C6.53135 4.15716 6.66145 4.34011 6.72812 4.54903C6.80303 4.78421 6.87167 4.94316 6.93382 5.02577C7.12149 5.27616 7.36494 5.47935 7.64484 5.61924C7.92474 5.75912 8.2334 5.83184 8.54631 5.83163V6.63837C8.12976 6.63882 7.7183 6.54691 7.34154 6.36926C6.96478 6.19161 6.63209 5.93263 6.36743 5.61097L6.08638 7.2063L6.91765 7.90414L7.81415 10.3677L7.05592 10.6436L6.23312 8.3833L4.86582 7.23578C4.75362 7.14517 4.66777 7.02614 4.6172 6.89109C4.56662 6.75603 4.55319 6.60988 4.57828 6.46787L4.78354 5.30418L4.51052 5.50262L3.65263 6.68358L3 6.20926L3.9482 4.90411L3.95513 4.90895V4.90884ZM6.32794 3.61337C6.11399 3.61337 5.90881 3.52838 5.75753 3.3771C5.60624 3.22581 5.52125 3.02063 5.52125 2.80668C5.52125 2.59274 5.60624 2.38756 5.75753 2.23627C5.90881 2.08499 6.11399 2 6.32794 2C6.54189 2 6.74707 2.08499 6.89835 2.23627C7.04964 2.38756 7.13462 2.59274 7.13462 2.80668C7.13462 3.02063 7.04964 3.22581 6.89835 3.3771C6.74707 3.52838 6.54189 3.61337 6.32794 3.61337ZM5.12927 8.92967L3.83292 10.4744L3.21505 9.95619L4.41537 8.52597L4.71622 7.64663L5.43859 8.25163L5.12927 8.92967Z"
            fill="#00B424"
          />
        </svg>
      )
    case STATUS_KEYS.STAYING:
      return (
        <svg
          className="cockpit-status__svg"
          width="14"
          height="14"
          viewBox="0 0 12 12"
          fill="none"
        >
          <path
            d="M6 12C9.31371 12 12 9.31371 12 6C12 2.68629 9.31371 0 6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12Z"
            fill="#FFD7ED"
          />
          <g clipPath="url(#cockpitStayClip)">
            <path
              d="M3.6 1.75636L4.44655 2.16655L4.35927 2.45455H6.42764C6.35782 2.26255 6.288 2.088 6.20945 1.92218L7.13455 1.76509C7.20436 1.97455 7.27418 2.20145 7.344 2.45455H9.76145V3.25745H4.32436V2.55927C4.15855 3.048 3.95782 3.51055 3.73091 3.96436V9.92509H2.81455V5.43927C2.61382 5.70982 2.40436 5.97164 2.18618 6.22473L1.88073 5.24727C2.64873 4.26109 3.22473 3.10036 3.6 1.75636ZM4.83055 3.58909H9.12436V5.36945H4.83055V3.58909ZM8.24291 4.70618V4.26109H5.72073V4.70618H8.24291ZM9.81382 5.70982V7.35055H9.29891V7.848H7.52727V9C7.52727 9.55855 7.22182 9.84655 6.61091 9.84655H5.64218L5.44145 8.96509C5.73818 9 6.02618 9.01745 6.29673 9.01745C6.49745 9.01745 6.60218 8.93018 6.60218 8.77309V7.848H4.80436V7.35055H4.18473V5.70982H9.81382ZM5.05745 7.07127H8.94109V6.49527H5.05745V7.07127Z"
              fill="#EB2F96"
            />
          </g>
          <defs>
            <clipPath id="cockpitStayClip">
              <rect width="11" height="11" fill="white" />
            </clipPath>
          </defs>
        </svg>
      )
    case STATUS_KEYS.OFFLINE:
      return (
        <svg
          className="cockpit-status__svg"
          width="14"
          height="14"
          viewBox="0 0 12 12"
          fill="none"
        >
          <path
            d="M6 12C9.31371 12 12 9.31371 12 6C12 2.68629 9.31371 0 6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12Z"
            fill="#E3E3E3"
          />
          <path
            d="M5.99609 2.90039C6.04979 2.90039 6.10242 2.91297 6.15039 2.9375C6.19838 2.96214 6.24093 2.99829 6.27539 3.04102C6.30973 3.08365 6.33672 3.13333 6.35449 3.18652C6.37217 3.23958 6.38081 3.29599 6.38086 3.35254V7.52441L7.43848 6.23438C7.50301 6.15584 7.59319 6.10442 7.69336 6.09766C7.76878 6.09266 7.84122 6.11351 7.90332 6.1543L7.96191 6.20117L7.96582 6.20508L7.8916 6.27246L7.96582 6.20605L7.99219 6.23535L7.99707 6.24023C8.06548 6.32661 8.10039 6.43981 8.09961 6.55371C8.09877 6.66761 8.062 6.78013 7.99219 6.86523L6.27344 8.96191C6.20376 9.04682 6.1048 9.09975 5.99609 9.09961C5.88731 9.09932 5.78817 9.04615 5.71875 8.96094L4.01074 6.86426V6.86328C3.97568 6.82129 3.94826 6.77261 3.92969 6.71973C3.91092 6.66623 3.90103 6.60913 3.90039 6.55176C3.89978 6.49432 3.90819 6.43685 3.92578 6.38281C3.94332 6.32893 3.96944 6.2786 4.00391 6.23535C4.03854 6.19199 4.0814 6.15581 4.12988 6.13086C4.17847 6.10587 4.23169 6.09255 4.28613 6.09277C4.3406 6.09301 4.39397 6.10645 4.44238 6.13184C4.49064 6.15716 4.53305 6.19373 4.56738 6.2373H4.56641L5.61133 7.51855V3.35254C5.61142 3.24742 5.64282 3.14346 5.70215 3.06055C5.76196 2.97716 5.84934 2.91791 5.94922 2.90332L5.95605 2.90234L5.98828 2.90039L5.99609 2.89941V2.90039Z"
            fill="#9F9F9F"
            stroke="#9F9F9F"
            strokeWidth="0.2"
          />
        </svg>
      )
    default:
      return null
  }
}

const TopFilterBar: React.FC<TopFilterBarProps> = ({
  selectedDate,
  onChangeDate,
  summary,
  activeStatus,
  onChangeStatus,
  onClickMapPoi,
  onClickSettings,
  refreshIntervalMs,
  onChangeRefreshInterval,
  onRefreshNow,
  isMock
}) => {
  // 首次点击选中状态时弹一次气泡提示
  const [showTip, setShowTip] = useState(false)
  const tipShownRef = useRef(false)
  const tipTimerRef = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(tipTimerRef.current), [])

  // 不可选未来日期
  const disabledDate = (current: dayjs.Dayjs | null) =>
    !!current && current > dayjs().endOf('day')

  const handleClickStatus = (key: StatusKey) => {
    const willSelect = activeStatus !== key
    onChangeStatus(willSelect ? key : null)
    // 进入页面后首次「选中」某状态 → 弹一次「再次点击取消筛选」，3s 后消失
    if (willSelect && !tipShownRef.current) {
      tipShownRef.current = true
      setShowTip(true)
      window.clearTimeout(tipTimerRef.current)
      tipTimerRef.current = window.setTimeout(() => setShowTip(false), 3000)
    }
  }

  return (
    <div className="cockpit-filter-bar">
      <div className="cockpit-filter-bar__left">
        <DatePicker
          value={selectedDate}
          allowClear={false}
          disabledDate={disabledDate}
          onChange={(date) => date && onChangeDate(date)}
        />
      </div>

      <div className="cockpit-filter-bar__center">
        {summary &&
          summary.isRealtime &&
          REALTIME_STATUS_META.map((meta) => {
            const count = getRealtimeCount(summary, meta.key)
            const active = activeStatus === meta.key
            return (
              <button
                key={meta.key}
                type="button"
                className={`cockpit-status cockpit-status--${meta.key} ${
                  active ? 'is-active' : ''
                }`}
                onClick={() => handleClickStatus(meta.key)}
              >
                <StatusIcon statusKey={meta.key} />
                <span className="cockpit-status__label">{meta.label}</span>
                <span className="cockpit-status__count">
                  {count}
                  {meta.withTotal && (
                    <em className="cockpit-status__total">
                      /{summary.totalMembers}
                    </em>
                  )}
                </span>
                {active && showTip && (
                  <span className="cockpit-status__tip">再次点击取消筛选</span>
                )}
              </button>
            )
          })}

        {summary && !summary.isRealtime && (
          <div className="cockpit-status cockpit-status--static">
            <span className="cockpit-status__dot cockpit-status__dot--blue" />
            <span className="cockpit-status__label">当日活跃</span>
            <span className="cockpit-status__count">
              {summary.activeToday}
              <em className="cockpit-status__total">/{summary.totalMembers}</em>
            </span>
          </div>
        )}

        {/* 刷新控件：立即刷新 + 自动频率（仅实时态展示） */}
        {summary && summary.isRealtime && (
          <RefreshControl
            value={refreshIntervalMs}
            onChange={onChangeRefreshInterval}
            onRefresh={onRefreshNow}
          />
        )}
      </div>

      <div className="cockpit-filter-bar__right">
        {/* 告警铃铛（未读角标 + 点击弹出告警列表），位于定位设置/地图点位左侧 */}
        <AlertBell />
        {isMock && (
          <Tag color="orange" className="cockpit-filter-bar__mock">
            mock 数据
          </Tag>
        )}
        <Button type="primary" ghost onClick={onClickMapPoi}>
          地图点位
        </Button>
        <Button type="primary" ghost onClick={onClickSettings}>
          设置
        </Button>
      </div>
    </div>
  )
}

export default TopFilterBar
